"use strict";
/**
 * The tap pad at /app/win-prob/<league>/<gameId>/tap (ai/TAP_PAD_PLAN.md).
 *
 * **Timing.** A tap's time is the touch event's own timestamp, taken on
 * pointerdown: click fires after the finger lifts, and on some phones hundreds
 * of milliseconds later. Each tap carries the clock offset in force, from the
 * shortest of five round trips to /win-prob/clock, refreshed every minute.
 *
 * **Plays.** SNAP opens a play, result and yardage taps refine it while it
 * runs, DONE closes it (services/tap-play.ts). On open the pad rebuilds that
 * state from the server's recent taps plus its own unsent ones, so a reload
 * mid-drive keeps the open play and play numbers never repeat.
 *
 * **Timers.** This view runs its own send, clock-sync and status timers. They
 * are not market polling (ai/GOTCHAS.md §2): they are started in openTapPad
 * and stopped in closeTapPad, nowhere else, and never touch the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupTapPad = setupTapPad;
exports.openTapPad = openTapPad;
exports.closeTapPad = closeTapPad;
exports.isTapPadOpen = isTapPadOpen;
const win_prob_1 = require("../api/win-prob");
const tap_queue_1 = require("../services/tap-queue");
const parked_hold_1 = require("./parked-hold");
const tap_parked_1 = require("../ui/tap-parked");
const tap_play_1 = require("../services/tap-play");
const tap_pad_1 = require("../ui/tap-pad");
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
const uuid_1 = require("../services/uuid");
const FLUSH_MS = 500;
const SYNC_MS = 60000;
const SYNC_SAMPLES = 5;
const REFUSAL_MS = 2500;
/** As the NBA pads: a dead connection must not hold the game's shared queue (tap ids are idempotent). */
const SEND_TIMEOUT_MS = 5000;
let pad = null;
const parkedHold = new parked_hold_1.ParkedHold();
const body = () => document.getElementById("win-prob-body");
const toHistory = (t) => ({
    tapId: t.tapId,
    kind: t.kind, // the football pad only ever holds football taps
    side: t.side,
    value: t.value,
    playSeq: t.playSeq,
    undoesTapId: t.undoesTapId,
    atMs: Date.parse(t.clientTs),
});
/** Drops taps the server refused from the history: the play, UNDO and play numbers skip them (N8 §19.3). */
function dropParked(current) {
    const parked = current.queue.parkedIds;
    if (parked.size)
        current.history = current.history.filter((t) => !parked.has(t.tapId));
}
function refreshStatus() {
    if (!pad)
        return;
    dropParked(pad);
    const status = body()?.querySelector(".tap-pad-status");
    const refused = pad.refusal && pad.refusal.until > Date.now() ? pad.refusal.text : null;
    (0, tap_parked_1.showParked)(status?.querySelector(".tap-parked"), pad.queue.parked.length);
    const text = status?.querySelector(".tap-status-text");
    if (status && text) {
        status.classList.toggle("tap-refused", refused !== null ? !pad.refusal?.ok : pad.queue.storageFailing);
        if (refused !== null)
            text.textContent = refused;
        else
            text.innerHTML = (pad.queue.storageFailing ? "taps not saved on phone · " : "") + (0, tap_pad_1.renderTapStatus)({
                pending: pad.queue.size,
                offsetMs: pad.clock?.offsetMs ?? null,
                rttMs: pad.clock?.rttMs ?? null,
                syncedAgoS: pad.clock ? Math.round((Date.now() - pad.clock.at) / 1000) : null,
                error: pad.queue.lastError,
                conflicts: pad.queue.conflicts.length,
            });
    }
    const state = (0, tap_play_1.derivePlay)(pad.history);
    const play = body()?.querySelector(".tap-play-state");
    if (play)
        play.innerHTML = (0, tap_pad_1.renderPlayState)(state, pad.teams);
    // Colour only: the pad must not reflow under a finger.
    body()?.querySelector(".tap-try")?.classList.toggle("tap-try-due", (0, tap_play_1.tryDue)(pad.history, state));
    const history = body()?.querySelector(".tap-history");
    if (history)
        history.innerHTML = (0, tap_pad_1.renderTapHistory)(pad.history, state.undone, pad.teams);
}
function refuse(current, text) {
    current.refusal = { text, until: Date.now() + REFUSAL_MS };
    navigator.vibrate?.([40, 40, 40]);
    refreshStatus();
}
async function syncClock(current) {
    const samples = [];
    for (let i = 0; i < SYNC_SAMPLES; i++) {
        try {
            const t0 = Date.now();
            const serverMs = await (0, win_prob_1.fetchServerClock)();
            samples.push({ t0, t1: Date.now(), serverMs });
        }
        catch {
            break; // offline: keep the last good offset
        }
    }
    const best = (0, tap_queue_1.estimateClockOffset)(samples);
    if (best && pad === current)
        current.clock = { ...best, at: Date.now() };
    refreshStatus();
}
function flush(current) {
    void current.queue.flush().then(() => {
        if (pad === current)
            refreshStatus();
    });
}
/** pointerdown on a pad button: record, queue, send. */
function onPointerDown(event) {
    if (!pad)
        return;
    if (parkedHold.press(event))
        return;
    const btn = event.target.closest(".tap-btn");
    const kind = btn?.dataset.tapKind;
    if (!btn || !(0, tap_vocabulary_1.isTapKind)(kind))
        return;
    event.preventDefault(); // no text selection, no ghost click, no double-tap zoom
    const atMs = performance.timeOrigin + event.timeStamp;
    dropParked(pad);
    const state = (0, tap_play_1.derivePlay)(pad.history);
    const decision = (0, tap_play_1.decideTap)(kind, state);
    if (!decision.accept)
        return refuse(pad, decision.reason);
    const side = tap_vocabulary_1.SIDED_TAP_KINDS.has(kind) ? (btn.dataset.side ?? null) : null;
    const value = kind === "yards" ? Number(btn.dataset.value) : null;
    let undoesTapId = null;
    if (kind === "undo") {
        const target = (0, tap_play_1.undoTarget)(pad.history, state);
        if (!target)
            return refuse(pad, "nothing to undo");
        undoesTapId = target.tapId;
    }
    pad.refusal = null;
    const tap = {
        tapId: (0, uuid_1.uuid)(),
        kind,
        side,
        undoesTapId,
        value,
        playSeq: decision.playSeq,
        clientTs: new Date(atMs).toISOString(),
        clientOffsetMs: pad.clock?.offsetMs ?? null,
        clientRttMs: pad.clock?.rttMs ?? null,
    };
    pad.queue.add(tap);
    pad.history.push({ ...toHistory(tap), atMs });
    btn.classList.add("tap-flash");
    window.setTimeout(() => btn.classList.remove("tap-flash"), 150);
    navigator.vibrate?.(15);
    refreshStatus();
    flush(pad);
}
function onClick(event) {
    if (pad && event.target.closest(".tap-pad-exit"))
        pad.onExit();
}
/** The release that ends a full hold on the parked count copies, inside the gesture. */
function onPointerUp(event) {
    if (!parkedHold.release(event) || !pad)
        return;
    const current = pad;
    void (0, parked_hold_1.copyParked)(current.queue).then(({ ok, text }) => {
        if (pad !== current)
            return;
        current.refusal = { text, until: Date.now() + REFUSAL_MS, ok };
        refreshStatus();
    });
}
const onPointerCancel = (event) => parkedHold.cancel(event);
const onOnline = () => {
    if (pad)
        flush(pad);
};
/** Once, from setupWinProbPage: the listeners sit on the shared page body. */
function setupTapPad() {
    body()?.addEventListener("pointerdown", onPointerDown);
    body()?.addEventListener("pointerup", onPointerUp);
    body()?.addEventListener("pointercancel", onPointerCancel);
    body()?.addEventListener("click", onClick);
    window.addEventListener("online", onOnline);
}
function openTapPad(league, gameId, onExit) {
    closeTapPad();
    const current = {
        league,
        gameId,
        queue: tap_queue_1.TapQueue.shared(`tap-pad:${league}:${gameId}`, (taps) => (0, win_prob_1.postTaps)(league, gameId, taps), tap_queue_1.browserTapStorage, Date.now, SEND_TIMEOUT_MS),
        teams: { home: "HOME", away: "AWAY" },
        history: [],
        refusal: null,
        clock: null,
        timers: [],
        onExit,
    };
    pad = current;
    const root = body();
    if (root)
        root.innerHTML = (0, tap_pad_1.renderTapPad)(current.teams, `${league.toUpperCase()} ${gameId} · tap pad`);
    refreshStatus();
    // Team names from the scoreboard listing, which needs no live tracker for the game.
    void (0, win_prob_1.fetchWinProbGames)(league)
        .then(({ games }) => {
        const game = games.find((g) => g.gameId === gameId);
        if (!game || pad !== current)
            return;
        current.teams = { home: game.home.abbreviation, away: game.away.abbreviation };
        const title = `${game.away.abbreviation} @ ${game.home.abbreviation} · tap pad`;
        const r = body();
        if (r)
            r.innerHTML = (0, tap_pad_1.renderTapPad)(current.teams, title);
        refreshStatus();
    })
        .catch(() => undefined); // the pad works without names
    // Unsent taps first, then the server's record merged in: the open play and
    // the play numbers survive a reload.
    current.history = current.queue.unsent.map(toHistory);
    void (0, win_prob_1.fetchTaps)(league, gameId)
        .then((stored) => {
        if (pad !== current)
            return;
        const known = new Set(current.history.map((t) => t.tapId));
        const merged = [...current.history, ...stored.filter((t) => !known.has(t.tapId)).map(toHistory)];
        current.history = merged.sort((a, b) => a.atMs - b.atMs);
        refreshStatus();
    })
        .catch(() => undefined); // offline: the pad still works from local state
    void syncClock(current);
    current.timers.push(window.setInterval(() => flush(current), FLUSH_MS), window.setInterval(() => void syncClock(current), SYNC_MS), window.setInterval(refreshStatus, 1000));
    flush(current); // taps left unsent before a reload go out now
}
function closeTapPad() {
    if (!pad)
        return;
    for (const t of pad.timers)
        window.clearInterval(t);
    void pad.queue.flush(); // last attempt; anything left stays in storage
    parkedHold.reset();
    pad = null;
}
function isTapPadOpen() {
    return pad !== null;
}
